<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useElementBounding, useWindowSize } from '@vueuse/core';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';

import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

// [{label, icon, value}]
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'faded',
  },
  label: {
    type: String,
    default: null,
  },
  // Mostra um campo de busca no topo da lista de opções.
  searchable: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const searchTerm = ref('');
const visibleOptions = computed(() => {
  const options = props.options || [];
  return props.searchable && searchTerm.value.trim()
    ? picoSearch(options, searchTerm.value, ['label'])
    : options;
});

const selected = defineModel({
  type: [String, Number],
  required: true,
});

const triggerRef = ref(null);
const dropdownRef = ref(null);

const { top } = useElementBounding(triggerRef);
const { height } = useWindowSize();
const { height: dropdownHeight } = useElementBounding(dropdownRef);

const selectedOption = computed(() => {
  return props.options?.find(o => o.value === selected.value) || {};
});

const labelToRender = computed(
  () => props.label || (props.hideLabel ? null : selectedOption.value.label)
);

// The option's own icon leads the label; the chevron always trails it so the
// control reads as a dropdown. Without a label the chevron is the only icon.
const leadingIcon = computed(() => {
  if (props.hideIcon) return null;
  return (
    selectedOption.value.icon ||
    (labelToRender.value ? null : 'i-lucide-chevron-down')
  );
});

const dropdownPosition = computed(() => {
  const DROPDOWN_MAX_HEIGHT = 340;
  // Get actual height if available or use default
  const menuHeight = dropdownHeight.value
    ? dropdownHeight.value + 20
    : DROPDOWN_MAX_HEIGHT;
  const spaceBelow = height.value - top.value;
  return spaceBelow < menuHeight ? 'bottom-0' : 'top-0';
});

const updateSelected = newValue => {
  selected.value = newValue;
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle }">
      <slot name="trigger" :toggle="toggle">
        <Button
          ref="triggerRef"
          type="button"
          sm
          slate
          :variant
          :icon="leadingIcon"
          :label="labelToRender"
          @click="toggle"
        >
          <template v-if="labelToRender" #default>
            <span class="min-w-0 truncate">{{ labelToRender }}</span>
            <span class="flex-shrink-0 i-lucide-chevron-down size-4" />
          </template>
        </Button>
      </slot>
    </template>
    <DropdownBody
      ref="dropdownRef"
      class="min-w-56 z-50"
      :class="dropdownPosition"
      strong
    >
      <div v-if="searchable" class="relative">
        <Icon class="absolute size-4 left-2 top-2" icon="i-lucide-search" />
        <input
          v-model="searchTerm"
          autofocus
          class="p-1.5 pl-8 text-n-slate-11 bg-n-alpha-1 rounded-lg w-full"
          :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        />
      </div>
      <DropdownSection class="[&>ul]:max-h-72">
        <li
          v-if="searchable && !visibleOptions.length"
          class="px-2 py-1.5 text-sm text-n-slate-11 select-none"
        >
          {{ t('COMBOBOX.EMPTY_STATE') }}
        </li>
        <template v-for="option in visibleOptions" :key="option.value">
          <li
            v-if="option.disabled"
            class="px-2 py-1.5 text-xs font-medium text-n-slate-10 select-none"
          >
            {{ option.label }}
          </li>
          <DropdownItem
            v-else
            :label="option.label"
            :icon="option.icon"
            @click="updateSelected(option.value)"
          />
        </template>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
